import { useMutation } from '@apollo/client'
import {
  CommerceHubPaymentToken,
  commerceHubPaymentTokenCreate,
  setVaultPaymentMethodOnCartMutation,
  SetVaultPaymentMethodOnCartResponse,
  deriveCcType, setCardPaymentMethodOnCartMutation
} from '../utils'
import { useState } from 'react'
import { useCheckout } from 'lib/modules/checkout'

interface Params {
  onCreatePaymentTokenSuccess?: () => void
}

interface CommerceHubCardDetails {
  cardholderName: string
  ccNumber: string
  ccExpMonth: string
  ccExpYear: string
  ccCid: string
  ccType?: string
}

export const useCommerceHub = (params?: Params) => {
  const { cartId, placeOrder, lockCheckout, unlockCheckout } = useCheckout()

  const [createPaymentToken] = useMutation<{
    commerceHubPaymentTokenCreate: CommerceHubPaymentToken
  }>(commerceHubPaymentTokenCreate, {
    onCompleted: params?.onCreatePaymentTokenSuccess
  })

  const [setVaultTokenOnCart] = useMutation<{
    setPaymentMethodOnCart: SetVaultPaymentMethodOnCartResponse
  }>(setVaultPaymentMethodOnCartMutation, {
    onCompleted: params?.onCreatePaymentTokenSuccess
  })

  const [setCardOnCart] = useMutation<{
    setPaymentMethodOnCart: SetVaultPaymentMethodOnCartResponse
  }>(setCardPaymentMethodOnCartMutation, {
    onCompleted: params?.onCreatePaymentTokenSuccess
  })

  const [isLoading, setLoading] = useState(false)

  const submitForm = (values: CommerceHubCardDetails) => {
    setLoading(true)
    const ccType = deriveCcType(values.ccNumber)
    createPaymentToken({
      variables: {
        ...values,
        ccType
      }
    })
  }

  const placeVaultOrder = async (params: { publicHash: string, onError: (e: Error) => void }) => {
    try {
      lockCheckout()
      await setVaultTokenOnCart({
        variables: {
          ...params,
          cartId
        }
      })

      unlockCheckout()
      placeOrder()
    } catch (e) {
      params.onError(e)
      unlockCheckout()
    }
  }

  const placeCardOrder = async (params: CommerceHubCardDetails & { onError: (e: Error) => void }) => {
    try {
      lockCheckout()
      const ccType = deriveCcType(params.ccNumber)
      await setCardOnCart({
        variables: {
          ...params,
          ccType,
          cartId
        }
      })

      unlockCheckout()
      placeOrder()
    } catch (e) {
      params.onError(e)
      unlockCheckout()
    }
  }

  return {
    loading: isLoading,
    createPaymentToken,
    submitForm,
    placeVaultOrder,
    placeCardOrder
  }
}
